import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { rupees, formatINR } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  TrendUp, Coins, Package, UsersThree, Percent, Receipt, Wallet, ChartLineUp,
} from "@phosphor-icons/react";

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#6B6B6B]">{label}</div>
          <div className="text-2xl font-medium text-[#1A1A1A] mt-2 tabular-nums">{value}</div>
          {sub && <div className="text-xs text-[#8A8A8A] mt-1">{sub}</div>}
        </div>
        <div className="h-10 w-10 rounded-md bg-[#F2EBE1] flex items-center justify-center text-[#C5A059]">
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user, activeBranch } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/dashboard${activeBranch ? `?branch_id=${activeBranch}` : ""}`).then(({ data }) => setD(data)).catch(() => {});
  }, [activeBranch]);

  if (!d) return <div className="p-8 text-[#8A8A8A]">Loading dashboard…</div>;

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title={`Good day, ${user?.name?.split(" ")[0]}`} subtitle="Here's what's happening across your showroom today." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <Kpi icon={Receipt} label="Today's Sales" value={rupees(d.today_sales)} sub={`${d.today_count} invoices`} testid="kpi-today-sales" />
        <Kpi icon={TrendUp} label="This Month" value={rupees(d.month_sales)} sub={`${d.total_sales_count} total invoices`} testid="kpi-month-sales" />
        <Kpi icon={Package} label="Available Stock" value={d.available_stock} sub={`Valuation ${rupees(d.stock_valuation)}`} testid="kpi-stock" />
        <Kpi icon={Coins} label="Active Schemes" value={d.active_schemes} sub={`${d.total_customers} customers`} testid="kpi-schemes" />
        <Kpi icon={Wallet} label="Expenses (Month)" value={rupees(d.month_expenses || 0)} sub={`All time ${rupees(d.total_expenses || 0)}`} testid="kpi-expenses" />
        <Kpi icon={ChartLineUp} label="Profit (Month)" value={<span className={d.month_profit < 0 ? "text-[#9D2A2A]" : "text-[#2D6A4F]"}>{rupees(d.month_profit || 0)}</span>} sub={`Sales − expenses − purchases ${rupees(d.month_purchases || 0)}`} testid="kpi-profit" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid="chart-sales-trend">
          <div className="text-sm font-semibold text-[#1A1A1A] mb-4">Sales — Last 7 Days</div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.trend}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C5A059" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEADF" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#8A8A8A" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8A8A8A" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => rupees(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E6E2D6" }} />
              <Area type="monotone" dataKey="sales" stroke="#C5A059" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid="card-gold-rates">
          <div className="text-sm font-semibold text-[#1A1A1A] mb-4">Today's Gold Rate (per gram)</div>
          <div className="space-y-3">
            {Object.entries(d.gold_rates || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-[#F2EBE1] pb-3">
                <span className="text-sm text-[#4A4A4A] font-medium">{k} Gold</span>
                <span className="text-lg tabular-nums text-[#8A6D2F] font-medium">{rupees(v).replace(".00", "")}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-[#B8860B] bg-[#FFF8E1] rounded-md px-3 py-2">
            <Percent size={14} /> {d.pending_discounts} discount approvals pending
          </div>
        </Card>
      </div>

      <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid="chart-top-products">
        <div className="text-sm font-semibold text-[#1A1A1A] mb-4">Top Selling Items (by value)</div>
        {d.top_products.length === 0 ? (
          <div className="text-sm text-[#8A8A8A] py-8 text-center">No sales recorded yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.top_products}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEADF" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8A8A8A" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8A8A8A" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => rupees(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E6E2D6" }} />
              <Bar dataKey="value" fill="#C5A059" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}
